// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { observable, action, reaction, computed } from 'mobx'
import {
  DeliveryValues,
  IBooking,
  IConfirmedBooking,
  IConfirmedBookingResponse,
  IPaymentForm,
  JwtUserTokenPayload
} from './bookingTypes'
import { IStores } from '.'
import { getFromApi, postToApi, showToastError } from '../services/apiService'
import { PaymentMethod } from '@stripe/stripe-js'
import { TFunction } from 'i18next'
import qs from 'query-string'

import { PlaceType } from '../components/UFOMap/components/types'
import { geocodeByPlaceId } from '../components/UFOMap/components/UFOPlaceAutocomplete'
import { ICoordinates } from '../components/UFOMap/UFOMap'
import jwtDecode from 'jwt-decode'
import { IUser } from './RegisterTypes'
import { bookingEventType, sendBookingAnalyticEvent } from '../services/analyticsService'
import configurations from '../utils/config/configurations'

const build_BOOKINGUPDATE_URL = (booking: IBooking) =>
  `/v4/reserve/bookings/${booking.product.reference}/${booking.location.reference}/${booking.schedule.startAt}/${booking.schedule.endAt}`
const build_BOOKINGCONFIRM_URL = (
  booking: IBooking,
  connectedToken: string | null
) => `/v4/reserve/bookings${connectedToken ? `/${connectedToken}` : ''}`

class BookingStore {
  store: IStores

  @observable loading = false
  @observable selectedBookingIndex: number | null = null;
  @observable selectedBooking: IBooking | null = null
  @observable confirmedBooking: IConfirmedBooking | null = null
  @observable confirmationBackgroundImageUrl: string | null = null
  @observable isFeedbackRequired = false

  @observable driver: IUser | null = null
  @observable selectedMileageValue = 0
  @observable selectedInsuranceValue: string | null = null
  @observable selectedOneWayReturnValue = false
  @observable selectedOneWayReturnLocationValue: string | null = null
  @observable selectedDeliveryType: DeliveryValues | null = null
  @observable selectedDeliveryValue: string | null = null
  @observable selectedDeliveryPlace: PlaceType | null = null
  @observable selectedDeliveryCoordinates: ICoordinates | null = null
  @observable selectedDeliveryLongitude: number | null = null

  constructor(store: IStores) {
    this.store = store

    reaction(
      () => {
        return {
          proposedBookings:
            this.store.carsStore.proposedBookings?.bookings.length || -1
        }
      },
      data => {
        console.debug('BookingStore.reaction - clear selected booking', data)
        this.selectBooking(null, null)
      },
      { fireImmediately: false }
    )

    reaction(
      () => this.store.paymentStore.lastVoucherCheckDate,
      data => {
        console.debug('BookingStore.reaction - update selected booking', data)
        this.updateBooking(null)
      },
      { fireImmediately: false }
    )
  }

  @computed get bookingQueries(): string | null {
    if (this.selectedBooking === null) {
      return null
    }
    const product = this.selectedBooking.product
    const location = this.selectedBooking.alternative?.isLocationChanged
      ? this.selectedBooking.alternative.location ||
      this.selectedBooking.location
      : this.selectedBooking.location
    const schedule = this.selectedBooking.alternative?.isScheduleChanged
      ? this.selectedBooking.alternative.schedule ||
      this.selectedBooking.schedule
      : this.selectedBooking.schedule

    return qs.stringify({
      product: product.reference,
      location: location.reference,
      startAt: schedule.startAt,
      endAt: schedule.endAt,
      partnerCode: this.store.registerStore.partnerCode,
      token: this.store.registerStore.connectedToken
    })
  }

  @action reset = () => {
    this.loading = true
    this.selectedBooking = null
    this.selectedInsuranceValue = null
    this.selectedOneWayReturnValue = false
    this.selectedOneWayReturnLocationValue = null
    this.selectedMileageValue = 0
    this.loading = false
  }

  @action selectBooking = (booking: IBooking | null, index: number | null) => {
    this.loading = true

    if (booking === null) {
      this.selectedBooking = null
    } else if (
      this.selectedBooking &&
      booking.vehicleModel.reference ===
      this.selectedBooking.vehicleModel.reference &&
      booking.alternative?.reference ===
      this.selectedBooking.alternative?.reference
    ) {
      this.selectedBooking = null
    } else {
      this.selectedBooking = booking
      this.selectedBookingIndex = index

      sendBookingAnalyticEvent('select_item', this.store.productsStore.selectedProduct, this.store.locationsStore.selectedCity, this.store.locationsStore.selectedLocation,
        this.store.calendarStore.selectedStartAt, this.store.calendarStore.selectedEndAt, this.store.carsStore.proposedBookings?.bookings, index, booking)

      if (this.selectedBooking.options?.mileage?.value) {
        this.selectedMileageValue = this.selectedBooking.options.mileage.value
      }

      if (this.selectedBooking.options?.insurances?.value) {
        this.selectedInsuranceValue = this.selectedBooking.options.insurances.value
      }

      if (this.selectedBooking.options?.deliveries?.value) {
        this.selectedDeliveryValue = this.selectedBooking.options.deliveries.value
      }

      if (this.selectedBooking.options?.deliveries?.type) {
        this.selectedDeliveryType = this.selectedBooking.options.deliveries.type
      }
    }

    this.loading = false
  }

  @action selectMileage = async (mileageValue: number): Promise<void> => {
    this.selectedMileageValue = mileageValue
    this.updateBooking('book_step_2_select_mileage')
  }

  @action selectInsurance = async (
    insuranceValue: string | null
  ): Promise<void> => {
    this.selectedInsuranceValue = insuranceValue
    this.updateBooking('book_step_2_select_protection')
  }

  @action selectOneWayReturnValue = async (
    isSelected: boolean
  ): Promise<void> => {
    this.selectedOneWayReturnValue = isSelected
  }

  @action selectOneWayReturnLocationValue = async (
    val: string | null
  ): Promise<void> => {
    this.selectedOneWayReturnLocationValue = val
    await this.updateBooking('book_step_2_select_one_way_return_location_value')
  }

  @action selectDeliveryType = async (
    deliveryType: DeliveryValues
  ): Promise<void> => {
    this.selectedDeliveryType = deliveryType
  }

  @action selectDeliveryValue = async (
    deliveryValue: string | null
  ): Promise<void> => {
    this.selectedDeliveryValue = deliveryValue
  }

  @action selectDeliveryPlace = async (
    deliveryPlace: PlaceType | null
  ): Promise<void> => {
    if (deliveryPlace?.place_id) {
      this.selectedDeliveryPlace = deliveryPlace
      const coordinates: google.maps.GeocoderResult[] = await geocodeByPlaceId(
        deliveryPlace?.place_id
      )
      this.selectedDeliveryCoordinates = {
        latitude: coordinates[0].geometry.location.lat(),
        longitude: coordinates[0].geometry.location.lng()
      }
      this.selectedDeliveryValue = coordinates[0].formatted_address
      this.selectedDeliveryType = DeliveryValues.DELIVERY_ADDRESS
      this.updateBooking('book_step_2_select_delivery')
    }
  }

  // eslint-disable-next-line complexity
  @action updateBooking = async (eventType: bookingEventType | null): Promise<IBooking | null> => {
    if (!this.selectedBooking) return null

    this.loading = true

    const filters = {
      vehicleModelReference: this.selectedBooking.vehicleModel?.reference,
      alternativeReference:
        this.selectedBooking.alternative?.reference || undefined,
      priceCurrency: this.selectedBooking?.price?.currency || 'EUR',
      deliveryType:
        this.selectedDeliveryType ||
        this.selectedBooking.options?.deliveries?.type,
      deliveryAddress:
        this.selectedDeliveryValue ||
        this.selectedBooking.options?.deliveries?.value,
      deliveryLatitude:
        this.selectedDeliveryCoordinates?.latitude ||
        this.selectedBooking.options?.deliveries?.latitude,
      deliveryLongitude:
        this.selectedDeliveryCoordinates?.longitude ||
        this.selectedBooking.options?.deliveries?.longitude,

      mileageValue:
        this.selectedMileageValue ||
        this.selectedBooking.options?.mileage?.value,
      insuranceValue:
        this.selectedInsuranceValue ||
        this.selectedBooking.options?.insurances?.value,
      oneWayValue: this.selectedOneWayReturnLocationValue,
      voucherOrPartnerCode: this.store.registerStore.partnerCode
        ? this.store.registerStore.partnerCode
        : this.store.paymentStore.isVoucherCodeValid &&
          this.store.paymentStore.formValues.voucherCode !== ''
          ? this.store.paymentStore.formValues.voucherCode?.toUpperCase()
          : undefined
      // referralAmountUsed: this.store.paymentStore.shouldUseLoyalty
    }

    const response = await getFromApi(
      build_BOOKINGUPDATE_URL(this.selectedBooking),
      filters
    )

    const bookings =
      response && response.status === 200 ? response.data.bookings : null

    if (bookings && bookings.length > 0) {
      this.selectedBooking = bookings[0]

      if (eventType) {
        sendBookingAnalyticEvent(eventType, this.store.productsStore.selectedProduct, this.store.locationsStore.selectedCity, this.store.locationsStore.selectedLocation,
          this.store.calendarStore.selectedStartAt, this.store.calendarStore.selectedEndAt, null, null, bookings[0])
      }
      this.loading = false

      return bookings[0]
    }
    this.loading = false

    return null
  }

  performConfirmBooking = async (
    booking: IBooking,
    paymentForm: IPaymentForm,
    paymentMethod?: PaymentMethod,
    paymentIntentId?: string,
    purchaseOrder?: string
  ): Promise<IConfirmedBookingResponse | null> => {
    const bookingToConfirmed = {
      ...this.selectedBooking,
      accounts: { driver: paymentForm?.driver },
      payment: { method: paymentMethod?.id, intent: paymentIntentId, purchaseOrder }
    }
    sendBookingAnalyticEvent('add_payment_info',
      this.store.productsStore.selectedProduct,
      this.store.locationsStore.selectedCity,
      this.store.locationsStore.selectedLocation,
      this.store.calendarStore.selectedStartAt,
      this.store.calendarStore.selectedEndAt,
      this.store.carsStore.proposedBookings?.bookings,
      this.selectedBookingIndex,
      this.selectedBooking)

    let scope: string | null = null

    if (this.store.registerStore.connectedToken) {
      const decoded: JwtUserTokenPayload = jwtDecode<JwtUserTokenPayload>(
        this.store.registerStore.connectedToken
      )
      scope = decoded.data.scope || 'ops'
    }

    const response = await postToApi(

      build_BOOKINGCONFIRM_URL(booking, scope === 'ops' ? this.store.registerStore.connectedToken : null),
      bookingToConfirmed
    )

    return response && response.status === 200 ? response.data : null
  }

  // eslint-disable-next-line complexity
  @action confirmBooking = async (
    data: IPaymentForm,
    t: TFunction
  ): Promise<boolean> => {
    console.debug('bookingStore.doConfirmBooking')

    if (!this.selectedBooking) {
      console.error('bookingStore.missing selectedBooking')

      return false
    }

    if (!data) {
      await showToastError(t('bookingPay:confirmBookingMissingInformation'))

      return false
    }

    this.loading = true

    let bookingConfirmedResponse = await this.performConfirmBooking(
      this.selectedBooking,
      this.store.paymentStore.paymentFormValues(this.store.registerStore.user),
      this.store.paymentStore.paymentMethod ?? undefined,
      undefined,
      this.store.paymentStore.purchaseOrder ?? undefined
    )

    if (!bookingConfirmedResponse) {
      await showToastError(
        t('bookingPay:confirmBookingMissingConfirmationResponse')
      )
      await this.store.paymentStore.createPaymentMethod()
      this.loading = false

      return false
    }

    if (
      bookingConfirmedResponse.paymentIntentRequireAction === true &&
      bookingConfirmedResponse.paymentIntentClientSecret
    ) {
      if (!this.store.paymentStore.stripe) {
        console.error('bookingStore.missing stripe')

        return false
      }

      if (!this.store.paymentStore.paymentMethod) {
        await showToastError(t('bookingPay:confirmBookingMissingPaymentMethod'))

        return false
      }

      let intentId: string | undefined
      let intentStatus: string | undefined
      let intentError: any | undefined

      if (bookingConfirmedResponse.paymentIntentClientSecret.startsWith('seti_')) {
        const setupResult = await this.store.paymentStore.stripe.confirmCardSetup(
          bookingConfirmedResponse.paymentIntentClientSecret
        )

        if (setupResult.error) {
          await showToastError(
            setupResult.error.message ||
            t('bookingPay:confirmBookingMissingPaymentIntent')
          )
          await this.store.paymentStore.createPaymentMethod()
          this.loading = false

          return false
        }

        intentId = setupResult.setupIntent?.id
        intentStatus = setupResult.setupIntent?.status
        intentError = setupResult.error
      } else {
        const paymentResult = await this.store.paymentStore.stripe.handleCardAction(
          bookingConfirmedResponse.paymentIntentClientSecret
        )

        if (paymentResult.error) {
          await showToastError(
            paymentResult.error.message ||
            t('bookingPay:confirmBookingMissingPaymentIntent')
          )
          await this.store.paymentStore.createPaymentMethod()
          this.loading = false

          return false
        }
        intentId = paymentResult.paymentIntent?.id
        intentStatus = paymentResult.paymentIntent?.status
        intentError = paymentResult.error
      }

      if (
        intentId &&
        ['requires_action', 'succeeded', 'requires_confirmation'].includes(intentStatus)
      ) {
        bookingConfirmedResponse = await this.performConfirmBooking(
          this.selectedBooking,
          data,
          undefined,
          intentId,
          this.store.paymentStore.purchaseOrder ?? undefined
        )

        if (!bookingConfirmedResponse) {
          await showToastError(
            t('bookingPay:confirmBookingMissingConfirmationResponse')
          )
          await this.store.paymentStore.createPaymentMethod()
          this.loading = false

          return false
        }
      } else {
        await showToastError(
          intentError?.error?.message ||
          t('bookingPay:confirmBookingMissingPaymentIntent')
        )
        await this.store.paymentStore.createPaymentMethod()
        this.loading = false

        return false
      }
    }

    this.confirmedBooking = bookingConfirmedResponse.booking
    this.confirmationBackgroundImageUrl =
      configurations.getRessource(bookingConfirmedResponse.confirmationBackgroundImageUrl) || null

    if (bookingConfirmedResponse.booking?.reference) {
      sendBookingAnalyticEvent('purchase',
        this.store.productsStore.selectedProduct,
        this.store.locationsStore.selectedCity,
        this.store.locationsStore.selectedLocation,
        this.store.calendarStore.selectedStartAt,
        this.store.calendarStore.selectedEndAt,
        this.store.carsStore.proposedBookings?.bookings,
        this.selectedBookingIndex,
        bookingConfirmedResponse.booking,
        data.voucherCode
      )

      if (['uft', 'ufodrive'].includes(configurations.UFO_APP_PLATFORM)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const dl: any = window.webDataLayer || window.dataLayer || []

        if (dl) {
          dl.push({
            'event': 'purchase_user',
            'email': `${data.driver.email}`,
            'phoneNumber': `${data.driver.phoneNumber}`,
            'name': `${data.driver.firstName} ${data.driver.lastName}`,
            'emailConsent': `${data.driver.emailConsent}`,
            'notificationsConsent': `${data.driver.notificationsConsent}`
          })
        }
      }
    }
    // this.loading = false keep loading true to avoid double request

    return true
  }
}

export default BookingStore
